import { useEffect } from "react";
import { ReactLenis } from "lenis/react";
import { motion } from "framer-motion";
import { Toaster } from "sonner";
import { Nav } from "../components/site/Nav";
import { Footer } from "../components/site/Footer";
import { FloatingWhatsApp } from "../components/site/FloatingWhatsApp";
import { WeddingServices } from "../components/site/WeddingServices";
import { WeddingPackages } from "../components/site/WeddingPackages";
import { WeddingFAQ } from "../components/site/WeddingFAQ";
import { WeddingForm } from "../components/site/WeddingForm";
import { IMG } from "../data/assets";
import { lineReveal } from "../lib/motion";
import { useLanguage } from "../lib/i18n";

const scrollEnquiry = () => document.querySelector("#wedding-enquiry")?.scrollIntoView({ behavior: "smooth" });

export default function Weddings() {
  const { t } = useLanguage();

  useEffect(() => {
    document.title = "Wedding Catering & Private Chef Weddings in Rutland & the East Midlands | Mediterranean Palate";
    window.scrollTo(0, 0);
    return () => { document.title = "Private Chef in Rutland & Stamford | Mediterranean Palate"; };
  }, []);

  return (
    <ReactLenis root options={{ lerp: 0.08, smoothWheel: true }}>
      <div className="grain-overlay" />
      <Toaster theme="dark" position="bottom-right" />
      <Nav />
      <main>
        {/* Wedding hero */}
        <section data-testid="wedding-hero" className="relative min-h-[92svh] flex items-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img src={IMG.weddingHero} alt="Mediterranean wedding reception" className="photo-grade w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#14180E] via-[#14180E]/75 to-[#14180E]/30" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#14180E] via-transparent to-transparent" />
          </div>
          <div className="relative z-10 max-w-[1600px] mx-auto px-5 md:px-10 pt-28 pb-16">
            <p className="font-mono uppercase tracking-[0.3em] text-[10px] sm:text-[11px] text-[--gold] mb-6">{t("wpage.eyebrow")}</p>
            <h1 className="font-display font-light tracking-tight leading-[0.98] text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-[--text] max-w-4xl">
              {[t("wpage.h1a"), t("wpage.h1b")].map((line, i) => (
                <span key={i} className="line-mask">
                  <motion.span custom={i} variants={lineReveal} initial="hidden" animate="visible" className="block">
                    {i === 1 ? <span className="italic text-[--gold]">{line}</span> : line}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.9 }} className="mt-8 text-[--text-2] text-base md:text-lg leading-relaxed max-w-2xl">
              {t("wpage.sub")}
            </motion.p>
            <motion.button initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1.05 }} onClick={scrollEnquiry} data-testid="wedding-hero-btn" className="mt-10 inline-flex items-center justify-center px-8 py-4 rounded-full bg-[--gold] text-[#14180E] font-mono text-[11px] uppercase tracking-[0.16em] hover:bg-[--text] transition-colors duration-400">
              {t("wpage.plan")}
            </motion.button>
          </div>
        </section>

        {/* Wedding dining intro */}
        <section className="tiled relative py-8 md:py-10 bg-[--bg]">
          <div className="max-w-[1000px] mx-auto px-5 md:px-10">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.9 }} className="bg-[--surface] border border-white/10 rounded-2xl p-8 md:p-14">
              <h2 className="font-display font-light tracking-tight leading-[1.05] text-3xl md:text-4xl text-[--text] mb-6">{t("wpage.introH2a")} <span className="italic text-[--gold]">{t("wpage.introH2b")}</span></h2>
              <p className="text-[--text-2] text-base leading-relaxed mb-4">{t("wpage.introP1")}</p>
              <p className="text-[--text-2] text-base leading-relaxed">{t("wpage.introP2")}</p>
            </motion.div>
          </div>
        </section>

        <WeddingServices />
        <WeddingPackages />
        <WeddingFAQ />
        <WeddingForm />

        {/* Final CTA */}
        <section className="relative py-8 md:py-10 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img src={IMG.canapes} alt="Wedding canapés" className="photo-grade w-full h-full object-cover" />
            <div className="absolute inset-0 bg-[#14180E]/85" />
          </div>
          <div className="relative z-10 max-w-[1000px] mx-auto px-5 md:px-10 text-center">
            <p className="font-display italic text-2xl md:text-3xl text-[--gold] mb-4">{t("wpage.finalQuote")}</p>
            <h2 className="font-display font-light tracking-tight text-3xl md:text-5xl text-[--text] mb-8">{t("wpage.finalH2")}</h2>
            <button onClick={scrollEnquiry} className="inline-flex items-center justify-center px-9 py-4 rounded-full bg-[--gold] text-[#14180E] font-mono text-[11px] uppercase tracking-[0.16em] hover:bg-[--text] transition-colors duration-400">{t("wpage.plan")}</button>
          </div>
        </section>
      </main>
      <Footer />
      <FloatingWhatsApp />
    </ReactLenis>
  );
}
